/**
 * The last line before a blank page.
 *
 * React unmounts the entire tree when a render throws, so without a boundary
 * every client-side error — a bad chunk, a stale cache, a null nobody expected
 * — produces the same thing: white. A visitor cannot tell that from a slow
 * connection or a dead site, and cannot report it usefully either.
 *
 * A class component because there is still no hook for this.
 *
 * `Reload the page` clears storage and any registered service worker before
 * reloading. That is aimed squarely at the failure this was written for: a
 * worker from a previous deployment serving a precache that names bundles which
 * no longer exist. A plain reload cannot fix that; this can.
 */
import { Component } from 'react';

export default class ErrorBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // Kept to the console rather than sent anywhere: there is no error
    // reporting service on this project, and the Privacy Policy promises no
    // third-party scripts.
    console.error('Gompa failed to render', error, info?.componentStack);
  }

  reset = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((r) => r.unregister()));
      }
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
    } catch {
      // Storage may be blocked entirely. Reload anyway — it is still the best
      // move available, and failing here must not stop it.
    }
    window.location.reload();
  };

  render() {
    if (!this.state.failed) return this.props.children;

    return (
      <div className="shell lost" role="alert">
        <h1 className="display display--md lost__title">Something went wrong</h1>
        <p className="lost__body">
          <span>This page could not be displayed.</span>
          <span>Reloading usually clears it.</span>
        </p>
        <button type="button" className="btn btn--gold btn--lg" onClick={this.reset}>
          Reload the page
        </button>
      </div>
    );
  }
}
